import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Tooltip,
  useBreakpointValue,
  useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { AddIcon } from "@chakra-ui/icons";
import { IoIosArrowDown } from "react-icons/io";
import { FaFile, FaUser } from "react-icons/fa";
import { MdCreditCard } from "react-icons/md";
import SearchTable2 from "components/Tables/SearchTable2";
import Loading from "components/Loading/Loading";
import { TrafficViolationFilter } from "views/Pages/TrafficViolation/TrafficViolationFilter";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { trafficViolationService } from "services/trafficViolationService";
import { ROUTES } from "constants/routes-path";
import { getEmptyPageResponse, toTablePageableData } from "utils/utils";
import { TRAFFIC_VIOLATION_LIST_URL_DEFAULTS } from "utils/listUrlSync";
import { useListUrlSync } from "hooks/useListUrlSync";
import { ListFiltersActiveAlert } from "components/ListFiltersActiveAlert/ListFiltersActiveAlert";
import TrafficViolationSpecificMenu from "./TrafficViolationSpecificMenu";
import { TrafficViolationGeneralActions } from "./TrafficViolationGeneralActions";

const TrafficViolationList = () => {
  const { t } = useTranslation();
  const { searchParams, setSearchParams, hasActiveFilters } = useListUrlSync(
    TRAFFIC_VIOLATION_LIST_URL_DEFAULTS,
  );
  const [response, setResponse] = useState(undefined);
  const [selectedRows, setSelectedRows] = useState([]);
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const [isLoadingResponse, setIsLoadingResponse] = useState(true);
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const orderByMap = { pagamento: "valor_pago" };
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handlePrintAit = async (id) => {
    try {
      setIsLoadingAction(true);

      const { data, headers } = await trafficViolationService.printAit(id);
      const fileName = headers["content-disposition"].match(
        /filename="(.+)"/,
      )[1];

      const url = URL.createObjectURL(new Blob([data]));
      Object.assign(document.createElement("a"), {
        href: url,
        download: fileName,
      }).click();

      URL.revokeObjectURL(url);
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingAction(false);
    }
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id)
        ? prev.filter((rowId) => rowId !== id)
        : [...prev, id],
    );
  };

  const toggleAllRows = (rows) => {
    const allIds = rows.map((r) => r.id);

    if (selectedRows.length === allIds.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(allIds);
    }
  };

  const handlePrintNa = async (id) => {
    try {
      setIsLoadingAction(true);

      const { data, headers } = await trafficViolationService.printNa(id);
      const fileName = headers["content-disposition"].match(
        /filename="(.+)"/,
      )[1];

      const url = URL.createObjectURL(new Blob([data]));
      Object.assign(document.createElement("a"), {
        href: url,
        download: fileName,
      }).click();

      URL.revokeObjectURL(url);
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handlePrintNp = async (id) => {
    try {
      setIsLoadingAction(true);

      const { data, headers } = await trafficViolationService.printNp(id);
      const fileName = headers["content-disposition"].match(
        /filename="(.+)"/,
      )[1];

      const url = URL.createObjectURL(new Blob([data]));
      Object.assign(document.createElement("a"), {
        href: url,
        download: fileName,
      }).click();

      URL.revokeObjectURL(url);
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleUndoCancel = async (id) => {
    try {
      setIsLoadingAction(true);

      await trafficViolationService.undoCancel(id);
      showToast({
        title: t("system.notifications.success"),
        status: "success",
      });
      fetchData();
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleBatchAction = async (serviceAction) => {
    try {
      setIsLoadingAction(true);

      for (const id of selectedRows) {
        const { data, headers } = await serviceAction(id);
        
        const fileName = headers["content-disposition"].match(/filename="(.+)"/)[1];

        const url = URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        Object.assign(link, {
          href: url,
          download: fileName,
        });

        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingAction(false);
      setSelectedRows([]);
    }
  };

  const handleBatchCancel = async () => {
    try {
      setIsLoadingAction(true);

      await trafficViolationService.batchCancel(selectedRows);

      showToast({
        title: t("system.notifications.success"),
        status: "success",
      });

      setSelectedRows([]);
      fetchData();
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleUndoDefer = async (id) => {
    try {
      setIsLoadingAction(true);

      await trafficViolationService.undoDefer(id);
      showToast({
        title: t("system.notifications.success"),
        status: "success",
      });
      fetchData();
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingAction(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: () => {
          const isAllSelected =
            response?._embedded?.content?.length > 0 &&
            selectedRows.length === response?._embedded?.content?.length;

          return (
            <Tooltip
              label={
                isAllSelected
                  ? t("traffic-violation.list.table.header.deselect-all")
                  : t("traffic-violation.list.table.header.select-all")
              }
              hasArrow
              placement="top"
            >
              <Box display="inline-block">
                <Checkbox
                  colorScheme="blue"
                  borderColor={useColorModeValue("gray.500", "gray.400")}
                  isChecked={isAllSelected}
                  onChange={() =>
                    toggleAllRows(response?._embedded?.content ?? [])
                  }
                />
              </Box>
            </Tooltip>
          );
        },
        accessor: "select",
        disableSortBy: true,
        maxWidth: 10,
        isHidden: !searchParams?.filters?.placa,
        Cell: ({ row }) => (
          <Checkbox
            colorScheme="blue"
            borderColor={useColorModeValue("gray.500", "gray.400")}
            isChecked={selectedRows.includes(row.original.id)}
            onChange={() => toggleRow(row.original.id)}
            onClick={(e) => e.stopPropagation()} 
          />
        ),
      },
      {
        Header: t("traffic-violation.list.table.header.ait-number"),
        accessor: "numero_ait",
        Cell: ({ row, value }) => (
          <Flex
            direction={{ base: "row-reverse", md: "row" }}
            justify={{ base: "flex-start", md: "center" }}
            align="center"
            gap={2}
          >
            <TrafficViolationSpecificMenu
              row={row}
              t={t}
              navigate={navigate}
              handlePrintAit={handlePrintAit}
              handlePrintNa={handlePrintNa}
              handlePrintNp={handlePrintNp}
              handleUndoCancel={handleUndoCancel}
              handleUndoDefer={handleUndoDefer}
            />
            <Flex align="center" gap={1}>
              {value}
              {row.original.nome_condutor && (
                <Tooltip
                  label={t("traffic-violation-inform-driver.driver-indicated")}
                  hasArrow
                >
                  <span>
                    <Icon as={FaUser} color="inherit" />
                  </span>
                </Tooltip>
              )}
            </Flex>
          </Flex>
        ),
      },
      {
        Header: t("traffic-violation.list.table.header.ait-vehicle-owner-name"),
        accessor: "nome_proprietario_veiculo_ait",
      },
      {
        Header: t("traffic-violation.list.table.header.vehicle-plate"),
        accessor: "placa_veiculo",
      },
      {
        Header: t("traffic-violation.list.table.header.infringement-date"),
        accessor: "data_infracao",
      },
      {
        Header: t("traffic-violation.list.table.header.infringement"),
        accessor: "infracao",
      },
      {
        Header: t("traffic-violation.list.table.header.value"),
        accessor: "valor_tipo_infracao",
      },
      {
        Header: t("traffic-violation.list.table.header.payment"),
        accessor: "pagamento",
      },
      {
        Header: t("traffic-violation.list.table.header.situation"),
        accessor: "situacao",
        disableSortBy: true,
      },
      {
        Header: t("system.labels.actions"),
        disableSortBy: true,
        actions: TrafficViolationGeneralActions(t, navigate),
      },
    ],
    [t, navigate, handlePrintAit, handlePrintNa, handlePrintNp, searchParams],
  );

  const newTrafficViolation = () => {
    navigate(ROUTES.transportManagementPath + ROUTES.addTrafficViolation);
  };

  const listDigitalCollections = () => {
    navigate(ROUTES.transportManagementPath + ROUTES.digitalCollections);
  };

  const importPaymentAction = () => {
    navigate(ROUTES.transportManagementPath + ROUTES.trafficViolations + ROUTES.violationConfirmPaymentsPath);
  }

  const newTrafficViolationVideoMonitoring = () => {
    navigate(
      ROUTES.transportManagementPath +
        ROUTES.addTrafficViolationVideoMonitoring,
    );
  };

  useEffect(() => {
    fetchData();
  }, [searchParams]);

  const fetchData = async () => {
    setIsLoadingResponse(true);
    if (response) setResponse(undefined);

    try {
      const data = await trafficViolationService.list(searchParams);
      setResponse(data);
    } catch (err) {
      setResponse(getEmptyPageResponse(searchParams));
      showToast({ title: t(err), status: "error" });
    } finally {
      setIsLoadingResponse(false);
    }
  };

  const updateSearchParams = (params = {}) => {
    const page =
      Math.min(params?.page ?? 1, response?.total_pages ?? Infinity) || 1;
    
    if ('filters' in params) {
      if (!params.filters?.placa) {
        setSelectedRows([]);
      }
    }
    
    if (params) setSearchParams({ ...searchParams, ...params, page: page });
    if (params.orderBy && params.orderBy in orderByMap)
      params.orderBy = orderByMap[params.orderBy];
  };

  const clearViolationFilters = () => {
    updateSearchParams({
      page: 1,
      filters: {
        placa: null,
        numero_ait: null,
        situacao: null,
        data_inicial: null,
        data_final: null,
      },
    });
  };

  return (
    <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
      <Flex
        w="100%"
        px="30px"
        justify="space-between"
        align="center"
        gap="4"
        direction={{ sm: "column", md: "row" }}
      >
        <Stack
          direction="row"
          spacing="10px"
          alignSelf={{ sm: "flex-start", lg: "auto" }}
        >
          <Button
            w="125px"
            variant="list-primary"
            leftIcon={<AddIcon fontSize="sm" />}
            alignSelf={{ sm: "flex-start", lg: null }}
            onClick={newTrafficViolation}
          >
            {t("traffic-violation.actions.new-ait")}
          </Button>
          <Button
            variant="list-primary"
            leftIcon={<AddIcon fontSize="sm" />}
            alignSelf={{ sm: "flex-start", lg: null }}
            onClick={newTrafficViolationVideoMonitoring}
          >
            {t("traffic-violation.actions.new-ait-monitoring")}
          </Button>
        </Stack>

        <Stack
          direction="row"
          spacing="10px"
          alignSelf={{ sm: "flex-start", lg: "auto" }}
        >
          <TrafficViolationFilter
            filterSnapshot={searchParams.filters}
            onFilter={(filters) => updateSearchParams({ filters })}
          />

          {selectedRows.length > 0 && searchParams?.filters?.placa && (
            <Menu>
              <MenuButton
                w="auto"
                px="4"
                variant="list-primary"
                rightIcon={<IoIosArrowDown />}
                as={Button}
              >
                {t("traffic-violation.actions.batch.title")} ({selectedRows.length})
              </MenuButton>

              <MenuList variant="light">
                <MenuItem 
                  onClick={() => handleBatchAction(trafficViolationService.printAit)} 
                  icon={<FaFile />}
                >
                  {t("traffic-violation.actions.batch.print-aits")}
                </MenuItem>
                
                <MenuItem 
                  onClick={() => handleBatchAction(trafficViolationService.printNa)} 
                  icon={<FaFile />}
                >
                  {t("traffic-violation.actions.batch.print-nas")}
                </MenuItem>
                
                <MenuItem 
                  onClick={() => handleBatchAction(trafficViolationService.printNp)} 
                  icon={<FaFile />}
                >
                  {t("traffic-violation.actions.batch.print-nps")}
                </MenuItem>
              </MenuList>
            </Menu>
          )}

          <Menu>
            <MenuButton
              w="125px"
              variant="list-primary"
              rightIcon={<IoIosArrowDown />}
              as={Button}
            >
              {t("system.labels.actions")}
            </MenuButton>

            <MenuList variant="light">
              <MenuItem onClick={newTrafficViolation} icon={<AddIcon />}>
                {t("traffic-violation.actions.new-ait")}
              </MenuItem>
              <MenuItem onClick={importPaymentAction}  icon={<MdCreditCard />}>
                {t("traffic-violation.actions.confirm-payments")}
              </MenuItem>
              <MenuItem icon={<MdCreditCard />}>
                {t("traffic-violation.actions.import-payments")}
              </MenuItem>
              <MenuItem icon={<MdCreditCard />}>
                {t("traffic-violation.actions.export-payments")}
              </MenuItem>
              <MenuItem onClick={listDigitalCollections} icon={<FaFile />}>
                {t("traffic-violation.actions.digital-collections")}
              </MenuItem>
            </MenuList>
          </Menu>
        </Stack>
      </Flex>

      <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
        <ListPageCardHeader
          title={t("traffic-violation.title")}
          subtitle={t("traffic-violation.description")}
        />

        <CardBody>
          <ListFiltersActiveAlert show={hasActiveFilters} onClear={clearViolationFilters} />
          {isLoadingResponse ? (
            <Loading maxH={{ sm: "100px", md: "350px" }} />
          ) : (
            <Box position="relative">
              <SearchTable2
                columnsData={columns}
                disableSearch={true}
                tableData={response?._embedded?.content ?? []}
                pageableData={toTablePageableData(
                  response?.page ?? {},
                  searchParams,
                )}
                onChange={(params) => {
                  updateSearchParams(params);
                }}
                onRowClick={(original) => {
                  navigate(
                    ROUTES.trafficViolationsDetailsPath.replace(
                      ":id",
                      original?.id,
                    ),
                  );
                }}
              />

              {isLoadingAction && (
                <Box
                  position="fixed"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  bg="rgba(255, 255, 255, 0.6)"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  zIndex="overlay"
                >
                  <Loading maxH={{ sm: "100px", md: "350px" }} />
                </Box>
              )}
            </Box>
          )}
        </CardBody>
      </Card>
    </Flex>
  );
};

export default TrafficViolationList;
